import type { Education } from '@seek/ca-myprofile-utils';
import { Qualifications } from '@seek/profile-read-ui';
import { useTranslations } from '@vocab/react';
import { Alert, Text, Stack } from 'braid-design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Card } from 'src/components/Card/Card';
import { CardAction } from 'src/components/Card/CardAction/CardAction';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingCardItem } from 'src/components/LoadingBlock/LoadingBlock';
import { EDUCATION } from 'src/components/Router';
import { updateRoute } from 'src/components/Router/actionCreators';
import { EDIT, ADD } from 'src/consts';
import { certsyLinkClicked } from 'src/modules/analytics/certsy/actionCreators';
import type { CertsyLinkPosition } from 'src/modules/analytics/certsy/eventNames';
import { useAppConfig } from 'src/modules/appConfig';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import {
  retryParent,
  getFailedActionsForParent,
} from 'src/modules/redux-blend';
import { EDUCATION_FORM } from 'src/reducers/forms';
import * as Schema from 'src/types/graphql';

import { EditEducation } from '../EditEducation/EditEducation';
import { trackUnconfirmedQualificationImpression } from '../actionCreators';
import {
  useAddUnconfirmedQualification,
  useGetConfirmedQualifications,
  useGetUnconfirmedQualifications,
  useRejectUnconfirmedQualification,
} from '../data';
import { qualificationFormOrEducationUpdateToConfirmQualificationInput } from '../utils';

import translations from './.vocab';

export const ReadEducation = () => {
  const dispatch = useDispatch();

  const { t } = useTranslations(translations);

  const { zone, language: languageCode, brand, isUnifiedSite } = useAppConfig();
  const blend = useTypedSelector((state) => state.blend);
  const showRetry = getFailedActionsForParent({ blend }, EDUCATION).length > 0;
  const page = useTypedSelector((state) => state.router.page);
  const mode = useTypedSelector((store) => store.router[EDUCATION].mode);
  const educationId = useTypedSelector(
    (store) => store.router[EDUCATION].params.id,
  );
  const isCreateResumePage = page === 'create-resume-page';

  const [addUnconfirmedQualificationFn] = useAddUnconfirmedQualification(ADD);
  const [rejectUnconfirmedQualificationFn] =
    useRejectUnconfirmedQualification();

  const [drawerTitle, setDrawerTitle] = useState<string>('');

  useEffect(() => {
    if (mode === 'EDIT') {
      if (educationId) {
        setDrawerTitle(t('Edit education'));
      } else {
        setDrawerTitle(t('Add education'));
      }
    }
  }, [mode, t, educationId]);

  const editQualificationFn = (id: string, confirmed: boolean) => {
    dispatch(
      updateRoute({
        to: EDUCATION,
        mode: EDIT,
        params: { id },
        analytics: {
          ...(!confirmed ? { origin: 'resume parsing nudge' } : {}),
          currentPageSectionState: 'edit',
        },
      }),
    );
  };

  const {
    confirmedQualifications,
    loading: confirmedQualificationsLoading,
    error: confirmedQualificationsError,
  } = useGetConfirmedQualifications();

  const {
    unconfirmedQualifications,
    loading: unconfirmedQualificationsLoading,
  } = useGetUnconfirmedQualifications();

  // Ensure ReadEducation is loaded after Profile component has been loaded.
  // This is to prevent race condition where unconfirmedQualificationTracking
  // is triggered before Profile data has been loaded into Redux.
  const isProfileLoading = useTypedSelector(({ profile }) => profile.isLoading);

  const isEmpty =
    [...confirmedQualifications, ...unconfirmedQualifications].length === 0;

  const isLoading = Boolean(
    isProfileLoading ||
      unconfirmedQualificationsLoading ||
      confirmedQualificationsLoading ||
      confirmedQualificationsError,
  );

  const retryErrors = () => dispatch(retryParent(EDUCATION));

  const cardAction = (
    <CardAction
      to={EDUCATION}
      mode={EDIT}
      data={{ automation: 'education-add' }}
      actionText={t('Add education')}
      isEmpty={isEmpty}
      analytics={{
        currentPageSectionState: 'add',
      }}
    />
  );

  const onCertsyLinkClick = useCallback(
    (linkPosition: CertsyLinkPosition, qualification: Education) => () => {
      dispatch(
        certsyLinkClicked({
          linkAction: 'verify',
          linkPosition,
          ...(qualification.verificationUrl
            ? { linkUrl: qualification.verificationUrl }
            : {}),
          credentialType: 'Qualification',
          currentPageSection: 'education',
        }),
      );
    },
    [dispatch],
  );

  return (
    <>
      <Card
        name={EDUCATION}
        title={t('Education')}
        emptyCardText={t('Tell employers about your education')}
        isEmpty={isEmpty}
        retryAction={showRetry ? retryErrors : undefined}
        cardAction={cardAction}
        loading={isLoading}
        loader={
          <Stack space="small">
            <LoadingCardItem />
            <LoadingCardItem />
            <LoadingCardItem />
          </Stack>
        }
      >
        <Stack space="small">
          {unconfirmedQualifications.length > 0 && (
            <>
              <Alert tone="promote">
                <Text>
                  {t('We found these qualifications in your resumé. Add...', {
                    brand,
                  })}
                </Text>
              </Alert>
              <Qualifications
                displayVariant="card"
                qualifications={unconfirmedQualifications.map(
                  (qualification) => ({
                    ...qualification,
                    confirmed: false,
                    addUnconfirmedQualification: (_id, contentRead) =>
                      addUnconfirmedQualificationFn(
                        {
                          qualification:
                            qualificationFormOrEducationUpdateToConfirmQualificationInput(
                              qualification,
                            ),
                          languageCode,
                          zone,
                          isUnifiedSite,
                        },
                        { contentRead },
                      ),
                    rejectUnconfirmedQualification: (id) =>
                      rejectUnconfirmedQualificationFn({
                        qualification: {
                          id,
                          dataType: Schema.UnconfirmedDataType.Education,
                          tracking: qualification.tracking || null,
                        },
                        languageCode,
                        zone,
                        isUnifiedSite,
                      }),
                    editQualification: editQualificationFn,
                    trackUnconfirmedQualificationImpression: (education) =>
                      dispatch(
                        trackUnconfirmedQualificationImpression(education),
                      ),
                    zone,
                  }),
                )}
              />
            </>
          )}

          {confirmedQualifications.length > 0 ? (
            <Qualifications
              displayVariant="card"
              qualifications={confirmedQualifications.map((qualification) => ({
                ...qualification,
                confirmed: true,
                editQualification: editQualificationFn,
                onVerifyClick: onCertsyLinkClick('main card', qualification),
                onCertsyClick: onCertsyLinkClick('pop up', qualification),
                showCertsyFunctionality: !isCreateResumePage,
                zone,
              }))}
            />
          ) : null}
        </Stack>
      </Card>
      <FormDrawer
        title={drawerTitle}
        component={EditEducation}
        card={EDUCATION}
        formName={EDUCATION_FORM}
      />
    </>
  );
};
